import { useState, useEffect } from 'react';
import { isObjectEmpty } from '../services/objectService';
import { cloneDeep } from 'lodash';

const defaultOptions = {
  mode: 'onSubmit',
  validationSchema: {},
};

/**
 * POSSIBLE OPTIONS (passedOptions):
 * - validationSchema: The validationSchema of the form
 * - initialValues: Initial values of the form
 * - change: Behaviour for change (onSubmit or onChange)
 * - allowDots: Allows dots to be used for field names (CAUTION: nested field validation not possible + no field names with TEMP_SIGN value)
 * - allowNull: Allow null/undefined values
 */
const useForm = (passedOptions) => {
  // Used for temporarily replacing dot if allowDots option is enabled
  const TEMP_SIGN = '$';
  const [options, setOptions] = useState(
    Object.assign({}, defaultOptions, passedOptions),
  );
  useEffect(() => {
    if (!passedOptions.change) return;
    setOptions((prev) => Object.assign(prev, passedOptions));
  }, [passedOptions]);

  const [values, setValues] = useState(options.initialValues || {});
  const [errors, setErrors] = useState({});

  const handleChange = ({ name, value }) => {
    if (value != null || options.allowNull) {
      setValues((values) => ({
        ...values,
        [name]: value,
      }));
    } else {
      setValues((values) => {
        const tempValues = { ...values };
        delete tempValues[name];
        return tempValues;
      });
    }
  };

  useEffect(() => {
    if (options.mode === 'onChange') {
      const valuesObject = values;
      // eslint-disable-next-line no-use-before-define
      validate(valuesObject).then((callbackErrors) => {
        setErrors(callbackErrors);
      });
    }
  }, [values]);

  /** This method will remove dots to resolve issues with dots in yup */
  const transformDotsToDollarSign = (valuesObject, validationSchema) => {
    // Allow dots by temporary replacing them
    const tempValueObject = {};
    Object.keys(valuesObject).forEach((key) => {
      if (key.includes('.')) {
        const replacedKey = key.replace(/\./g, TEMP_SIGN);
        tempValueObject[replacedKey] = valuesObject[key];
      } else {
        tempValueObject[key] = valuesObject[key];
      }
    });

    // Use a validation schema copy and replace
    const validationSchemaCopy = cloneDeep(validationSchema);
    validationSchemaCopy._nodes = validationSchemaCopy._nodes.map((field) =>
      field.replace(/\./g, TEMP_SIGN),
    );

    Object.keys(validationSchemaCopy.fields).forEach((key) => {
      if (key.includes('.')) {
        const replacedKey = key.replace(/\./g, TEMP_SIGN);
        validationSchemaCopy.fields[replacedKey] =
          validationSchemaCopy.fields[key];
        delete validationSchemaCopy.fields[key];
      }
    });

    return [tempValueObject, validationSchemaCopy];
  };

  /* TODO: fix to handle arrays
    for now if object is an array, its error
    obj has 'clients[0].first_name' key as a string.
    E.g {'clients[0].first_name': 'this is required'}
  */
  const validate = (valuesObject, schema) => {
    return new Promise((resolve, reject) => {
      let { validationSchema } = options || {};
      if (schema) validationSchema = schema;
      const tempErrors = {};

      let _valuesObject = valuesObject;
      let _validationSchema = validationSchema;

      if (options?.allowDots) {
        [_valuesObject, _validationSchema] = transformDotsToDollarSign(
          _valuesObject,
          _validationSchema,
        );
      }

      try {
        const validators = _validationSchema._nodes.map((field) => {
          let _field = field;
          if (options?.allowDots) {
            _field = field.replace(/\./g, TEMP_SIGN);
          }
          return _validationSchema.validateAt(_field, _valuesObject);
        });

        Promise.all(validators.map((p) => p.catch((e) => e))).then((errors) => {
          const inValidResults = errors.filter(
            (result) => result instanceof Error,
          );
          inValidResults.forEach((err) => {
            const { errors } = err || {};
            if (options?.allowDots && err?.path) {
              // Remove dollar sign
              const re = new RegExp('\\' + TEMP_SIGN, 'g');
              err.path = err.path.replace(re, '.');
            }
            const [firstError] = errors || [];

            tempErrors[err.path] = firstError;
          });
          resolve(tempErrors);
        });
      } catch (e) {
        reject(
          'There was an error in the useForm hook, no validation possible. Please make sure that if dots are used in field names that allowDots is on true. See the next log for the error itself.',
        );
        console.error(e);
      }
    });
  };

  const handleSubmit = async (callback) => {
    setOptions((values) => ({
      ...values,
      mode: 'onChange',
    }));

    const callbackErrors = await validate(values);
    setErrors(callbackErrors);
    if (isObjectEmpty(callbackErrors)) {
      callback(values);
    }
  };

  const resetForm = () => {
    if (passedOptions.mode !== 'onChange')
      setOptions({ ...options, mode: 'onSubmit' });
    setValues({});
    setErrors({});
  };

  return {
    handleChange,
    handleSubmit,
    resetForm,
    values,
    setValues,
    errors,
    setErrors,
    validate,
  };
};

export default useForm;
